import { ScanPackagesView } from "../scan_packages";
import AppIcons from "../../../icons";
import { CollectForDeliveryItemView } from "./item";
import ScanTransportationUnitModal from "../modals/scan_transportation_unit";
import { ViewEvent, ViewConstructor } from "../../../../core/view";
import { FortumApp } from "../../../fortumApp";
import { ModalResultType } from "../../../../core/modal";
import { ClientAction } from "../../../synchronizer";
import { Template } from "../../../../core/template";
import { splitItemCode, arrayMoveItem } from "../../../helpers";
import { emptyElement } from "../../../../core/helpers/dom";
import { TransportationUnit } from "../../../db/models";
import CollectConfirmModal from "../modals/collect_confirm";

export class CollectForDeliveryView extends ScanPackagesView {

    app: FortumApp;
    template = Template.lazy('/app/views/package_tracking/collect_for_delivery/index.html');
    // Item view
    itemViewClass = CollectForDeliveryItemView;

    private tupDoneButton: HTMLButtonElement;
    private transportationUnit: string;
    private transportationUnitSorting: string[] = ['*'];

    // Cache transportation units
    private transportationUnits: { [id: string]: TransportationUnit };
    
    // Items that user has scanned
    private items: { [tup: string]: CollectForDeliveryItemView[] } = {'*': []};

    // Hold scanned tags to prevent duplicates
    private scannedTags: string[] = [];

    public afterRender(): PromiseLike<any>
    {
        setTimeout(() => {
            this.codeInput.readOnly = false;
        });

        this.codeInput.addEventListener('blur', () => {
            this.focusInputForScanner();
        });

        return super.afterRender();
    }

    public beforeRender(): PromiseLike<any>
    {
        super.beforeRender();
        this.app.header.text = this.app.pgettext('package_tracking', 'Collect for delivery'),
        this.app.header.iconClass = AppIcons.PackageTracking_Collect;
        
        return this.app.db.TransportationUnit.toArray().then(tups => {
            this.transportationUnits = {};
            tups.forEach(tup => {
                this.transportationUnits[tup.id] = tup;
            });
        });
    }

    protected onCodeScanned(tag: string)
    {   
        this.codeInput.value = '';

        const [prefix, item] = splitItemCode(tag);

        if (prefix === 'TUP') {
            this.activateTransportationUnit(item);
        } else {
            // Prevent duplicate items
            if (this.scannedTags.indexOf(tag) < 0) {
                this.renderItem(tag);
                this.scannedTags.push(tag);
            }
        }
    }

    private activateTransportationUnit(code: string) {
        if (!(code in this.items)) {
            this.items[code] = [];
            // Add TUP to extra array for sorting
            this.transportationUnitSorting.unshift(code);
        } else {
            // Change sorting so that most recent TUP is at the top
            this.transportationUnitSorting = arrayMoveItem(this.transportationUnitSorting, code, 0);
        }
        this.transportationUnit = code;
        this.nextButton.disabled = true;
        this.tupDoneButton.disabled = false;
        this.tupDoneButton.classList.remove('hidden');
        this.refresh();
    }

    public deactivateCurrentTransportationUnit() {
        this.transportationUnit = null;
        this.nextButton.disabled = false;
        this.tupDoneButton.disabled = true;
        this.tupDoneButton.classList.add('hidden');

        this.focusInputForScanner();
        this.transportationUnitSorting = arrayMoveItem(this.transportationUnitSorting, '*', 0);
    }

    private renderItem(tag: string) {
        const renderedItem = new this.itemViewClass({fetch_tag: tag}) as CollectForDeliveryItemView;
        renderedItem.on('delete', view => this.removeItem(view));
        const tup = this.transportationUnit || '*';
        this.items[tup].unshift(renderedItem);
        this.app.renderPartialView(renderedItem).then(() => {
            this.refresh();
        });
    }

    private removeItem(item: CollectForDeliveryItemView) {
        Object.keys(this.items).some(key => {
            const index = this.items[key].indexOf(item);
            if (index >= 0) {
                this.items[key].splice(index, 1);
                const tag = item.item.qr_code;
                const tagIndex = this.scannedTags.indexOf(tag);
                if (tagIndex >= 0) {
                    this.scannedTags.splice(tagIndex, 1);
                }
                return true;
            }
        });
        this.refresh();
    }

    private refresh() {
        emptyElement(this.resultsList);
        this.transportationUnitSorting.forEach(key => {
            const groupLabel = key == '*' ? this.pgettext('Package tracking', 'Individual packages') : key;
            this.resultsList.insertAdjacentElement('beforeend', this.renderTransportationUnitHeader(groupLabel));
            this.items[key].forEach(item => item.render(this.resultsList));
        });

        // Enable / disable continue
        this.nextButton.disabled = this.transportationUnit ? true : false;
    }

    protected renderTransportationUnitHeader(name: string): HTMLElement {
        const header = document.createElement('li');
        header.classList.add('pt-scanned-list-heading');
        header.textContent = this.getTransportationUnitName(name);
        return header;
    }

    protected getTransportationUnitName(tup: string): string {
        return this.transportationUnits.hasOwnProperty(tup) ? this.transportationUnits[tup].name : tup
    }

    public onContinue()
    {
        this.nextButton.disabled = true;

        const confirmData = [];
        const messages = [];
        Object.keys(this.items).forEach(tup => {

            // Build data for confirmation modal
            this.items[tup].forEach(itemView => {
                const itemData = itemView.item;
                if (tup !== '*') {
                    itemData.transportation_unit_name = this.getTransportationUnitName(tup);
                }
                confirmData.push(itemData);
            });

            // Build messages for synchronization
            let tupData = tup;
            if (tup === '*') {
                tupData = null;
            }
            messages.push({
                action: ClientAction.PACKAGE_TRACKING.COLLECT_FOR_DELIVERY,
                data: {
                    transportation_unit: tupData,
                    items: this.items[tup].map(itemView => itemView.item.id)
                }
            });
        });

        // Confirm collection
        new CollectConfirmModal(confirmData).showIn(this.app).then(res => {

            if (res && res.type === ModalResultType.CANCEL) {
                this.nextButton.disabled = false;
                return;
            }

            const promises = messages.map(message => {
                return this.app.synchronizer.push(message, false, false);
            });
            
            return Promise.all(promises).then(() => {
                this.nextButton.disabled = false;
                if (promises.length > 0) {
                    this.app.toasts.show(this.pgettext('Message', 'Packages received successfully'));
                    history.go(-1);
                }
            }).then(() => this.nextButton.disabled = false, () => this.nextButton.disabled = false);

        });
    }

    private confirmCollect(transportationUnit: string, items: any[])
    {
        console.log('Confirm collect!')
        /* this.app.synchronizer.push({
            action: ClientAction.PACKAGE_TRACKING.COLLECT_FOR_DELIVERY,
            data: {
                transportation_unit: transportationUnit,
                items: items.map(item => item.id)
            }
        });

        this.app.toasts.show(this.pgettext('package_tracking', 'Packages collected successfully'));
        history.back();*/
    }
}